import { cn } from '../../lib/utils';
import { SORT_KEY_LABEL, SORT_KEYS, type SortKey } from './map-list-header-constants';

interface MapListHeaderProps {
  /** Total filtered count to display in the left label. */
  readonly count: number;
  /** Current sort field. */
  readonly sortKey: SortKey;
  /** Current sort direction. */
  readonly sortAsc: boolean;
  /** Fires with the newly-picked sort key. The caller (MapSidebar)
   *  resets direction to that key's sensible default -- this
   *  component doesn't know what "sensible" means per key. */
  readonly onSortKeyChange: (key: SortKey) => void;
  /** Flips the current sort direction without changing the key. */
  readonly onToggleDirection: () => void;
}

/**
 * List header bar above the metro list. Mirrors `_archive/map.html`
 * `.panel-list-head` (lines 211-225) for the outer chrome, but the
 * sort control itself was replaced (CR-05, 2026-07-08 -- Maria 3e)
 * with a real `<select>` dropdown offering 5 sort keys instead of a
 * single button that only cycled ratio<->name. A separate direction
 * toggle button sits next to the dropdown so the user can flip
 * asc/desc without hunting for a second click-to-cycle state.
 */
export function MapListHeader({
  count,
  sortKey,
  sortAsc,
  onSortKeyChange,
  onToggleDirection,
}: MapListHeaderProps) {
  const arrow = sortAsc ? '↑' : '↓';
  // CR-28 (Maria 2026-07-08 #3a): the sort control previously sat on ONE
  // row opposite the metro count (`justify-between`). In the ~288px
  // sidebar the long option labels (e.g. "Credentials-to-jobs ratio")
  // squeezed the count to two lines and cramped the dropdown. Stack it:
  // a compact label row (count + "SORT BY") on top, then a full-width
  // dropdown + direction toggle below — Maria's suggested "title above,
  // dropdown below" layout. Uses less horizontal room, no wrapping.
  return (
    <div
      className={cn(
        'flex flex-shrink-0 flex-col gap-1.5 border-b border-border-light px-5 pt-[10px] pb-2',
      )}
    >
      <div className="flex items-baseline justify-between">
        <span className="text-[12px] text-text-4">
          {count} {count === 1 ? 'metro' : 'metros'}
        </span>
        <span
          aria-hidden="true"
          className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-4"
        >
          Sort by
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <select
          id="map-sort-select"
          aria-label="Sort metros by"
          value={sortKey}
          onChange={(e) => onSortKeyChange(e.target.value as SortKey)}
          className="min-w-0 flex-1 rounded-md border-[1.5px] border-border bg-surface px-[9px] py-1 text-[11px] font-bold tracking-[0.01em] text-navy transition hover:border-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
        >
          {SORT_KEYS.map((key) => (
            <option key={key} value={key}>
              {SORT_KEY_LABEL[key]}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onToggleDirection}
          aria-label={`Sort direction: ${sortAsc ? 'ascending' : 'descending'} (click to toggle)`}
          className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md border-[1.5px] border-border bg-surface text-[12px] font-bold text-navy transition hover:border-navy hover:bg-navy hover:text-white"
        >
          {arrow}
        </button>
      </div>
    </div>
  );
}
